import { apiClient } from './authService';
import { buildDownloadUrl } from './downloadUrl';

const ENABLE_DIRECT_ARTIFACT_URLS =
  String(process.env.REACT_APP_ENABLE_DIRECT_ARTIFACT_URLS || '').toLowerCase() === 'true';

const toFormData = ({
  file,
  maskFile,
  sceneFile,
  textureFile,
  textureSettings,
  algorithm,
  backgroundRemoval,
  stepParameters,
  validateParameters,
  autoStart = true,
}) => {
  if (!file) {
    throw new Error('Pipeline creation requires an input file.');
  }

  const formData = new FormData();
  formData.append('file', file);
  if (maskFile) {
    formData.append('mask_file', maskFile);
  }
  if (sceneFile) {
    formData.append('scene_file', sceneFile);
  }
  if (textureFile) {
    formData.append('texture_file', textureFile);
  }
  if (textureSettings && Object.keys(textureSettings).length > 0) {
    formData.append('texture_settings', JSON.stringify(textureSettings));
  }
  formData.append('algorithm', algorithm);

  if (typeof backgroundRemoval === 'boolean') {
    formData.append('background_removal', String(backgroundRemoval));
  }

  if (stepParameters && Object.keys(stepParameters).length > 0) {
    formData.append('step_parameters', JSON.stringify(stepParameters));
  }

  if (typeof validateParameters === 'boolean') {
    formData.append('validate_parameters', String(validateParameters));
  }

  formData.append('auto_start', String(Boolean(autoStart)));

  return formData;
};

export const pipelineService = {
  async createSession({
    file,
    maskFile = null,
    sceneFile = null,
    textureFile = null,
    textureSettings = null,
    algorithm,
    backgroundRemoval = true,
    stepParameters = null,
    validateParameters = false,
    autoStart = true,
  }) {
    const formData = toFormData({
      file,
      maskFile,
      sceneFile,
      textureFile,
      textureSettings,
      algorithm,
      backgroundRemoval,
      stepParameters,
      validateParameters,
      autoStart,
    });

    const response = await apiClient.post('/pipeline/create', formData, {
      headers: {},
    });

    return response.data;
  },

  async prepareSession(options) {
    return this.createSession({ ...options, autoStart: false });
  },

  async startSession(sessionId, { algorithm, backgroundRemoval = true, stepParameters = null, validateParameters = false } = {}) {
    const payload = {
      algorithm,
      background_removal: backgroundRemoval,
      validate_parameters: validateParameters,
    };

    if (stepParameters && Object.keys(stepParameters).length > 0) {
      payload.step_parameters = stepParameters;
    }

    const response = await apiClient.post(`/pipeline/${sessionId}/start`, payload);
    return response.data;
  },

  async reworkSession(sessionId) {
    const response = await apiClient.post(`/pipeline/${sessionId}/rework`);
    return response.data;
  },

  async getStatus(sessionId) {
    const response = await apiClient.get(`/pipeline/${sessionId}/status`);
    return response.data;
  },

  async getPartialResults(sessionId, steps) {
    const params = steps && steps.length ? { steps: steps.join(',') } : undefined;
    const response = await apiClient.get(`/pipeline/${sessionId}/partial-results`, { params });
    return response.data;
  },

  async listFiles(sessionId, { includeFallback = true } = {}) {
    const response = await apiClient.get(`/pipeline/${sessionId}/files`, {
      params: { include_fallback: includeFallback },
    });
    return response.data;
  },

  async getDownloadInfo(sessionId, { includeMetadata = true, useConsolidated = true } = {}) {
    const response = await apiClient.get(`/pipeline/${sessionId}/download`, {
      params: {
        format: 'individual',
        include_metadata: includeMetadata,
        use_consolidated: useConsolidated,
      },
    });
    return response.data;
  },

  _resolveArtifactLocator(stepType, filename, artifactRef = null) {
    if (artifactRef && artifactRef.step_type && artifactRef.filename) {
      return {
        stepType: artifactRef.step_type,
        filename: artifactRef.filename,
      };
    }

    return { stepType, filename };
  },

  _resolveDirectFileUrl(fileEntry, purpose = 'preview') {
    if (!ENABLE_DIRECT_ARTIFACT_URLS) {
      return null;
    }

    if (!fileEntry || typeof fileEntry !== 'object') {
      return null;
    }

    const candidates =
      purpose === 'download'
        ? [
            fileEntry.signed_download_url,
            fileEntry.direct_download_url,
            fileEntry.signed_url,
            fileEntry.signed_preview_url,
          ]
        : [
            fileEntry.signed_preview_url,
            fileEntry.direct_preview_url,
            fileEntry.signed_url,
          ];

    return candidates.find(
      (value) => typeof value === 'string' && /^https?:\/\//i.test(value)
    ) || null;
  },

  _resolveFileIdentity(stepType, filenameOrFile, artifactRef = null) {
    const fileEntry =
      filenameOrFile && typeof filenameOrFile === 'object' ? filenameOrFile : null;
    const filename = fileEntry ? fileEntry.filename : filenameOrFile;
    const resolvedArtifactRef = artifactRef || fileEntry?.artifact_ref || null;
    const resolvedStepType = stepType || fileEntry?.stepType || fileEntry?.step_type || null;
    const locator = this._resolveArtifactLocator(
      resolvedStepType,
      filename,
      resolvedArtifactRef
    );
    return {
      fileEntry,
      locator,
      artifactRef: resolvedArtifactRef,
    };
  },

  async downloadFile(
    sessionId,
    stepType,
    filenameOrFile,
    {
      useFallback = true,
      artifactRef = null,
      directUrl = null,
      preferDirect = ENABLE_DIRECT_ARTIFACT_URLS,
    } = {}
  ) {
    const { fileEntry, locator } = this._resolveFileIdentity(
      stepType,
      filenameOrFile,
      artifactRef
    );
    const preferredDirectUrl =
      directUrl || this._resolveDirectFileUrl(fileEntry, 'download');

    if (preferDirect && preferredDirectUrl) {
      try {
        const directResponse = await fetch(preferredDirectUrl, {
          method: 'GET',
          credentials: 'omit',
        });
        if (directResponse.ok) {
          return await directResponse.blob();
        }
      } catch (_error) {
      }
    }

    const response = await apiClient.get(`/pipeline/${sessionId}/files/${locator.stepType}/${encodeURIComponent(locator.filename)}`, {
      params: {
        use_fallback: useFallback,
      },
      responseType: 'blob',
    });
    return response.data;
  },

  buildDownloadUrl(sessionId, stepType, filename, options = {}) {
    return this.buildFileUrl(sessionId, stepType, filename, options);
  },

  resolveFilePreviewUrl(sessionId, stepType, filenameOrFile, options = {}) {
    const { useFallback = true, artifactRef = null } = options;
    const { fileEntry } = this._resolveFileIdentity(stepType, filenameOrFile, artifactRef);
    const directUrl = this._resolveDirectFileUrl(fileEntry, 'preview');
    if (directUrl) {
      return directUrl;
    }
    return this.buildFileUrl(sessionId, stepType, filenameOrFile, { useFallback, artifactRef });
  },

  resolveFileDownloadUrl(sessionId, stepType, filenameOrFile, options = {}) {
    const { useFallback = true, artifactRef = null } = options;
    const { fileEntry } = this._resolveFileIdentity(stepType, filenameOrFile, artifactRef);
    const directUrl = this._resolveDirectFileUrl(fileEntry, 'download');
    if (directUrl) {
      return directUrl;
    }
    return this.buildFileUrl(sessionId, stepType, filenameOrFile, { useFallback, artifactRef });
  },

  buildFileUrl(sessionId, stepType, filenameOrFile, { useFallback = true, artifactRef = null } = {}) {
    const { locator } = this._resolveFileIdentity(stepType, filenameOrFile, artifactRef);
    if (!sessionId || !locator.stepType || !locator.filename) {
      return null;
    }

    return buildDownloadUrl(
      `/pipeline/${sessionId}/files/${locator.stepType}/${encodeURIComponent(locator.filename)}`,
      {
        use_fallback: useFallback ? 'true' : null,
      }
    );
  },


};

export default pipelineService;
